import React, { useEffect, useState } from 'react';
import { getTemplates, createTemplate, deleteTemplate } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FileText, Mail, MessageSquare, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export const TemplatesTab = () => {
    const [templates, setTemplates] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [form, setForm] = useState({ nom: '', type_template: 'email', sujet: '', contenu: '', declencheur: '' });

    const load = async () => {
        try {
            const res = await getTemplates();
            setTemplates(res.data);
        } catch {
            setTemplates([]);
        }
    };
    useEffect(() => { load(); }, []);

    const handleCreate = async () => {
        if (!form.nom || !form.contenu) {
            toast.error('Veuillez remplir les champs obligatoires');
            return;
        }
        try {
            await createTemplate(form);
            toast.success('Template créé');
            setShowModal(false);
            setForm({ nom: '', type_template: 'email', sujet: '', contenu: '', declencheur: '' });
            load();
        } catch {
            toast.error('Erreur lors de la création');
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await deleteTemplate(deleteId);
            toast.success('Template supprimé');
            setDeleteId(null);
            load();
        } catch {
            toast.error('Erreur lors de la suppression');
        }
    };

    return (
        <div className="bg-white border border-[#E4E4E7]">
            <div className="p-4 border-b border-[#E4E4E7] flex items-center justify-between">
                <h3 className="font-semibold text-[#0A0A0A]">Templates de messages</h3>
                <Button onClick={() => setShowModal(true)} className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white rounded-sm" data-testid="new-template-btn">
                    <Plus className="w-4 h-4 mr-2" /> Nouveau template
                </Button>
            </div>
            {templates.length === 0 ? (
                <div className="text-center py-12 text-[#52525B]">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Aucun template</p>
                    <p className="text-sm mt-1">Créez des templates pour vos emails et SMS de relance</p>
                </div>
            ) : (
                <div className="divide-y divide-[#E4E4E7]">
                    {templates.map((t) => (
                        <div key={t.id} className="p-4" data-testid={`template-${t.id}`}>
                            <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                    <div className={`p-2 rounded-sm ${t.type_template === 'email' ? 'bg-[#0284C7]/10 text-[#0284C7]' : 'bg-[#16A34A]/10 text-[#16A34A]'}`}>
                                        {t.type_template === 'email' ? <Mail className="w-5 h-5" /> : <MessageSquare className="w-5 h-5" />}
                                    </div>
                                    <div>
                                        <p className="font-medium">{t.nom}</p>
                                        <p className="text-sm text-[#52525B]">{t.type_template}</p>
                                        {t.sujet && <p className="text-sm mt-1">Sujet: {t.sujet}</p>}
                                        <p className="text-sm text-[#52525B] mt-2 line-clamp-2">{t.contenu}</p>
                                    </div>
                                </div>
                                <Button variant="ghost" size="icon" onClick={() => setDeleteId(t.id)} className="text-[#DC2626]">
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={showModal} onOpenChange={setShowModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Nouveau template</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="field-label">Nom du template *</Label>
                            <Input value={form.nom} onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))} placeholder="Ex: Relance devis J+3" className="h-10 rounded-sm mt-1" />
                        </div>
                        <div>
                            <Label className="field-label">Type</Label>
                            <Select value={form.type_template} onValueChange={(v) => setForm((p) => ({ ...p, type_template: v }))}>
                                <SelectTrigger className="h-10 rounded-sm mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="email">Email</SelectItem>
                                    <SelectItem value="sms">SMS</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {form.type_template === 'email' && (
                            <div>
                                <Label className="field-label">Sujet</Label>
                                <Input value={form.sujet} onChange={(e) => setForm((p) => ({ ...p, sujet: e.target.value }))} placeholder="Sujet de l'email" className="h-10 rounded-sm mt-1" />
                            </div>
                        )}
                        <div>
                            <Label className="field-label">Contenu *</Label>
                            <Textarea value={form.contenu} onChange={(e) => setForm((p) => ({ ...p, contenu: e.target.value }))} rows={5} placeholder="Bonjour {prenom},&#10;&#10;Nous vous recontactons suite à..." className="rounded-sm mt-1" />
                            <p className="text-xs text-[#52525B] mt-1">
                                Variables disponibles: {'{prenom}'}, {'{nom}'}, {'{telephone}'}, {'{email}'}
                            </p>
                        </div>
                        <div>
                            <Label className="field-label">Déclencheur (optionnel)</Label>
                            <Input value={form.declencheur} onChange={(e) => setForm((p) => ({ ...p, declencheur: e.target.value }))} placeholder="Ex: 3 jours après envoi devis" className="h-10 rounded-sm mt-1" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowModal(false)}>Annuler</Button>
                        <Button onClick={handleCreate} className="bg-[#E85A24] hover:bg-[#CC4D1C] text-white">Créer</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>Êtes-vous sûr de vouloir supprimer ce template ?</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-[#DC2626] hover:bg-[#DC2626]/90">Supprimer</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
